<template>
    <!-- <pre class="bg-red-400 text-xs max-h-52 overflow-auto">{{ images }}</pre> -->
    <div class="w-full h-full flex flex-row">
        <!-- Sidebar for labels -->
        <div class="min-w-36 h-full bg-white/10 p-4 flex flex-col border-r border-white/20">
            <input v-model="searchText" type="text" placeholder="Search images..."
                class="mb-2 px-2 py-1 rounded border border-white/20 bg-white/15 text-white placeholder-slate-500 text-sm" />
            <span class="font-semibold mb-2 text-sm text-ui-active">Labels</span>
            <!-- <span class="w-full span-item" @click="selectLabel({ id: 'unlabeled', name: 'Unlabeled' })"
                :class="{ 'font-bold': selectedLabelId === 'unlabeled', 'cursor-pointer': true }">Unlabeled</span> -->
            <LabelTree :editable="editable" :refreshLabels="refreshLabels" @select="selectLabel"
                @image-drop="onImageDrop" />
        </div>
        <!-- Main content -->
        <div class="min-w-36 flex-1 flex flex-col ml-2 @container">
            <div class="grid grid-cols-1 @sm:grid-cols-2 @md:grid-cols-3 @lg:grid-cols-4 @xl:grid-cols-5 @2xl:grid-cols-6 gap-4 overflow-auto">
                <ImageItem v-for="(image, i) in filteredImages" :key="image.id" :editable="editable"
                    v-model="images[images.findIndex(img => img.id === image.id)]" @select="selectImage"
                    @delete="deleteImageById" @image-copied="onImageCopied" />
            </div>
            <div class="flex-1"></div>
            <div v-if="editable" class="mt-4 flex flex-col @md:flex-row gap-2 @md:justify-between @md:items-center sticky bottom-0 py-2 ms-2 border-t border-white/20">
                <div class="flex flex-col @sm:flex-row gap-2">
                    <label class="bg-blue-600 text-white py-2 px-4 rounded cursor-pointer text-center whitespace-nowrap">
                        <input type="file" class="hidden" @change="onFileChange" accept="image/png" />
                        Add Image
                    </label>
                    <label class="bg-green-600 text-white py-2 px-4 rounded cursor-pointer text-center whitespace-nowrap">
                        <input type="file" class="hidden" @change="onZipChange" accept=".zip" />
                        Upload Zip
                    </label>
                </div>
                <form-button @click="downloadAndClear" color="primary" v-bind:class="{ hidden: !clearEnabled }">
                    Download All Images
                </form-button>
            </div>
        </div>
    </div>
</template>


<script setup>

import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import ImageItem from './ImageItem.vue';
import { useImageStore } from '../../stores/imageStore';
import LabelTree from './LabelTree.vue';

const imageStore = useImageStore();

const props = defineProps({
    clearEnabled: {
        type: Boolean,
        required: false,
        default: true,
    },
    editable: {
        type: Boolean,
        required: false,
        default: true,
    },
    refreshTrigger: {
        type: Number,
        required: false,
        default: 0,
    }
});

const emit = defineEmits(['close', 'select', 'zip-upload', 'imageDeleted', 'imageUpload']);

const images = ref([]);
const selectedLabelId = ref(null);
const searchText = ref('');
const refreshLabels = ref(0);

onMounted(async () => {
    try {
        fetchImages();
        document.addEventListener('keydown', handleKeydown);
    } catch (error) {
        console.error('Failed to open the database:', error);
    }
});

onUnmounted(() => {
    document.removeEventListener('keydown', handleKeydown);
});

// Watch for refresh trigger changes (e.g., from device file uploads)
watch(() => props.refreshTrigger, () => {
    fetchImages();
});

const sortedImages = computed(() => {
    return images.value.slice().sort((a, b) => a.filename.localeCompare(b.filename));
});

const allChildIds = ref([]);

watch(selectedLabelId, async (newVal) => {
    if (!newVal || newVal === 'all' || newVal === 'unlabeled') {
        allChildIds.value = [];
        return;
    }
    allChildIds.value = await imageStore.collectDescendantIds(newVal);
});

const filteredImages = computed(() => {
    let imgs = sortedImages.value;
    if (searchText.value) {
        const q = searchText.value.toLowerCase();
        imgs = imgs.filter(img => img.filename && img.filename.toLowerCase().includes(q));
    }
    if (!selectedLabelId.value || selectedLabelId.value === 'all') return imgs;
    if (selectedLabelId.value === 'unlabeled') {
        return imgs.filter(img => (img.labelId === null || img.labelId === undefined));
    }
    if (allChildIds.value.length > 0) {
        imgs = imgs.filter(img => allChildIds.value.includes(img.labelId));
    } else {
        imgs = [];
    }
    return imgs;
});

const close = () => {
    emit('close');
};

const selectImage = (image) => {
    emit('select', { src: image.src, filename: image.filename });
    close();
};

async function onImageDrop({ labelId, imageId }) {
    await imageStore.setImageLabelId(imageId, labelId);
    fetchImages();
    sortImages();
}

const onFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
        const reader = new FileReader();
        reader.onload = async (e) => {
            const base64String = e.target.result;
            let labelId = null;
            if (selectedLabelId.value && selectedLabelId.value !== 'all' && selectedLabelId.value !== 'unlabeled') {
                labelId = selectedLabelId.value;
            }
            // addImage can accept labelId as third argument
            await imageStore.addImage(base64String, file.name, labelId);
            await fetchImages();
            refreshLabels.value++;
            emit('imageUpload', file.name);
        };
        reader.readAsDataURL(file);
    }
};

const onZipChange = async (event) => {
    const file = event.target.files[0];
    // console.log('ZIP file selected:', file);
    if (file) {
        const jszip = new JSZip();
        const zip = await jszip.loadAsync(file);
        for (const zipFile of Object.keys(zip.files)) {
            // console.log('Processing file in ZIP:', filename);
            // if (zip.files[filename].dir) continue; // skip folders
            // split filename into labels and the last is the filename
            if (zipFile.endsWith('/') || !zipFile.endsWith('.png')) continue; // skip folders
            const parts = zipFile.split('/');
            const labelPath = parts.slice(0, -1);
            const filename = parts[parts.length - 1];
            let label = null;
            if (labelPath[0] === 'unlabeled' || labelPath.length === 0) {
                label = { id: null, name: '' };
            } else {
                label = await imageStore.getLabelByPathArray(labelPath);
            }
            const fileData = await zip.files[zipFile].async('base64');
            const base64String = `data:image/png;base64,${fileData}`;
            // console.log('File data length:', base64String);
            await imageStore.addImage(base64String, filename, label.id);
        }
        await fetchImages();
        refreshLabels.value++;
        emit('imageUpload', file.name);
    }
};

const downloadAndClear = async () => {
    const zip = new JSZip();

    for (const image of images.value) {
        const imageLabelPath = await imageStore.getLabelPathArray(image.labelId);
        const labelName = imageLabelPath.length > 0 ? imageLabelPath.join('/') : 'unlabeled';
        zip.file(`${labelName}/${image.filename}`, image.src.substr(image.src.indexOf(',') + 1), { base64: true });
    }

    const blob = await zip.generateAsync({ type: 'blob' });
    saveAs(blob, 'hasp_images.zip');
};


const fetchImages = async () => {
    await imageStore.load();
    images.value = [...imageStore.all];
    sortImages();
};

const sortImages = () => {
    images.value = images.value.slice().sort((a, b) => a.filename.localeCompare(b.filename));
};

const deleteImageById = async (id) => {
    await imageStore.removeImage(id);
    fetchImages();
    // Emit the imageDeleted event with the deleted image ID
    emit('imageDeleted', id);
};

const onImageCopied = () => {
    fetchImages();
};

const handleKeydown = (event) => {
    if (event.key === 'Escape') {
        close();
    }
};

const selectLabel = (label) => {
    selectedLabelId.value = label.id;
};

</script>

<style scoped>
.span-item {
    display: block;
    padding: 0.5rem 1rem;
    color: #cbd5e1;
}

.span-item:hover {
    background-color: rgba(255, 255, 255, 0.1);
    color: var(--ui-active);
}

.selected-span-item {
    font-weight: bold;
    background-color: rgba(255, 255, 255, 0.15);
    color: var(--ui-active);
}
</style>
